/**
 * Hierarchical (two-level) category filter tree.
 *
 * Top-level parents are rendered as accordion rows with a checkbox that
 * indicates:
 *   - checked       → every child slug is currently selected, OR the parent
 *                     is a leaf (no children) and its own slug is selected.
 *   - indeterminate → at least one but not all child slugs are selected
 *                     ("mixed" state surfaced via aria-checked="mixed").
 *   - unchecked     → no descendants are selected.
 *
 * Toggling a parent selects every descendant (or clears them). Toggling a
 * child toggles just that slug. Selected state lives as a flat slug list
 * in `useProductFilters` so API calls don't care about the tree shape.
 *
 * Expansion (which accordions are open) is local component state: by
 * default, parents whose descendants are currently selected are open.
 */

"use client";

import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { categoryDisplayLabel } from "../product-card-utils";
import { FilterSection } from "./FilterSection";

/* ─── Public types ─── */

export interface CategoryTreeChild {
  id: string;
  name: string;
  slug: string;
}

export interface CategoryTreeNode {
  id: string;
  name: string;
  slug: string;
  children: CategoryTreeChild[];
}

interface CategorySectionProps {
  /**
   * Hierarchical category tree. If empty (API failure or pre-seed state),
   * the section renders a flat fallback list from `fallbackNames`.
   */
  tree: CategoryTreeNode[];
  /** Flat list of category names, used only as a fallback when `tree` is empty. */
  fallbackNames?: string[];
  /** Selected slugs (flat — mix of parent and child slugs). */
  selected: string[];
  onToggle: (slug: string) => void;
  /** Replace the full selection in one op — used by the parent checkbox. */
  onReplaceSelection: (next: string[]) => void;
  isOpen: boolean;
  onToggleSection: (title: string) => void;
}

/* ─── Indeterminate checkbox helper ─── */

function ParentCheckbox({
  state,
  onChange,
  ariaLabel,
}: {
  state: "checked" | "unchecked" | "mixed";
  onChange: () => void;
  ariaLabel: string;
}) {
  // Radix CheckboxPrimitive.Root natively supports `checked: boolean | "indeterminate"`.
  // The shadcn wrapper forwards all props, so passing "indeterminate" sets
  // `data-state="indeterminate"` and `aria-checked="mixed"` automatically.
  const checked: boolean | "indeterminate" =
    state === "mixed" ? "indeterminate" : state === "checked";
  return (
    <Checkbox
      checked={checked}
      onCheckedChange={onChange}
      aria-label={ariaLabel}
      className="data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-primary"
    />
  );
}

/* ─── Component ─── */

export function CategorySection({
  tree,
  fallbackNames,
  selected,
  onToggle,
  onReplaceSelection,
  isOpen,
  onToggleSection,
}: CategorySectionProps) {
  // Normalise selection once so slug/name mismatches from legacy URLs (e.g.
  // `?category=Crochet`) still light up the right rows. We check `slug`
  // first (canonical), then fall back to a case-insensitive name match.
  const selectedSet = new Set(selected.map((s) => s.toLowerCase()));
  const isSelected = (slug: string, name: string) =>
    selectedSet.has(slug.toLowerCase()) || selectedSet.has(name.toLowerCase());

  // Default expansion: parents with at least one descendant selected.
  const defaultExpanded = () => {
    const set = new Set<string>();
    for (const p of tree) {
      if (
        isSelected(p.slug, p.name) ||
        p.children.some((c) => isSelected(c.slug, c.name))
      ) {
        set.add(p.id);
      }
    }
    return set;
  };
  const [expanded, setExpanded] = useState<Set<string>>(defaultExpanded);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Fallback: legacy flat list when the hierarchical tree hasn't loaded.
  if (tree.length === 0 && fallbackNames && fallbackNames.length > 0) {
    return (
      <FilterSection
        title="Product type"
        count={selected.length}
        isOpen={isOpen}
        onToggle={onToggleSection}
      >
        <div className="flex flex-col gap-2" role="group" aria-label="Product type filter">
          {fallbackNames.map((c) => (
            <label
              key={c}
              className="flex cursor-pointer items-center gap-3 py-1 text-sm text-foreground"
            >
              <Checkbox
                checked={selected.includes(c)}
                onCheckedChange={() => onToggle(c)}
              />
              <span>{categoryDisplayLabel(c)}</span>
            </label>
          ))}
        </div>
      </FilterSection>
    );
  }

  return (
    <FilterSection
      title="Product type"
      count={selected.length}
      isOpen={isOpen}
      onToggle={onToggleSection}
    >
      <div
        className="flex flex-col gap-1"
        role="group"
        aria-label="Product type filter"
      >
        {tree.map((parent) => {
          const childSlugs = parent.children.map((c) => c.slug);
          const selectedChildCount = parent.children.filter((c) =>
            isSelected(c.slug, c.name)
          ).length;
          const parentSelfSelected = isSelected(parent.slug, parent.name);

          // Parent state derivation:
          // - No children: behaves like a leaf — parent slug IS the selection.
          // - With children: "mixed" if SOME children selected; "checked" if
          //   ALL children selected (parent slug needn't be — children imply
          //   the parent).
          let parentState: "checked" | "unchecked" | "mixed";
          if (childSlugs.length === 0) {
            parentState = parentSelfSelected ? "checked" : "unchecked";
          } else if (selectedChildCount === childSlugs.length) {
            parentState = "checked";
          } else if (selectedChildCount > 0) {
            parentState = "mixed";
          } else {
            parentState = parentSelfSelected ? "checked" : "unchecked";
          }

          const isExpanded = expanded.has(parent.id);

          const handleParentToggle = () => {
            if (childSlugs.length === 0) {
              onToggle(parent.slug);
              return;
            }
            if (parentState === "checked" || parentState === "mixed") {
              // Deselect parent + every child.
              const stripped = selected.filter(
                (s) => s !== parent.slug && !childSlugs.includes(s)
              );
              onReplaceSelection(stripped);
            } else {
              // Select every child (parent slug left implicit — children
              // imply the parent, backend resolves it).
              const added = Array.from(new Set([...selected, ...childSlugs]));
              onReplaceSelection(added);
            }
          };

          return (
            <div
              key={parent.id}
              role="group"
              aria-label={categoryDisplayLabel(parent.name)}
              className="flex flex-col"
            >
              <div className="flex items-center gap-2 py-1.5">
                <ParentCheckbox
                  state={parentState}
                  onChange={handleParentToggle}
                  ariaLabel={`Select all ${parent.name}`}
                />
                {childSlugs.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => toggleExpand(parent.id)}
                    aria-expanded={isExpanded}
                    aria-controls={`cat-children-${parent.id}`}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${parent.name}`}
                    className="group flex flex-1 cursor-pointer items-center justify-between gap-2 rounded-sm py-1 text-left text-sm text-foreground hover:text-primary"
                  >
                    <span>{categoryDisplayLabel(parent.name)}</span>
                    <ChevronRight
                      className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                ) : (
                  <span className="flex-1 py-1 text-sm text-foreground">
                    {categoryDisplayLabel(parent.name)}
                  </span>
                )}
              </div>
              {childSlugs.length > 0 && isExpanded && (
                <div
                  id={`cat-children-${parent.id}`}
                  role="group"
                  aria-label={`${parent.name} subcategories`}
                  className="ml-6 flex flex-col gap-1 border-l border-border pl-3"
                >
                  {parent.children.map((child) => (
                    <label
                      key={child.id}
                      className="flex cursor-pointer items-center gap-3 py-1 text-sm text-foreground"
                    >
                      <Checkbox
                        checked={isSelected(child.slug, child.name)}
                        onCheckedChange={() => onToggle(child.slug)}
                        aria-label={`Filter by ${child.name}`}
                      />
                      <span>{categoryDisplayLabel(child.name)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </FilterSection>
  );
}
